// ─────────────────────────────────────────────────────────────────────────────
// Firebase / GA4 Configuration
// Centraliza as credenciais Firebase por ambiente.
//
// Fonte única: src/app/config/firebase-analytics.credentials.json
// DEV / HML / HEXT → development (Firebase demo + GA4 demo na web)
// master/main/production → production (GA4 Orbitta PDV na web; Firebase oficial carteira-3f277)
//
// Nativo (Android/iOS): SEMPRE carteira-3f277 via scripts/apply-firebase-analytics-config.js
// O GA4 nativo de produção vem do vínculo property Orbitta PDV (546470587) no Firebase Console — não troca google-services.
// ─────────────────────────────────────────────────────────────────────────────

import { EnvironmentType } from '@app/models/env';
import { environment } from '../../../environments/environment';
import firebaseAnalyticsCredentials from '../../config/firebase-analytics.credentials.json';
import firebaseAnalyticsProfile from '../../config/firebase-analytics.profile.json';

export interface FirebaseConfig {
  label?: string;
  ga4PropertyId?: string;
  ga4AccountId?: string;
  ga4WebStreamId?: string;
  ga4WebStreamUrl?: string;
  ga4AndroidStreamId?: string;
  ga4IosStreamId?: string;
  gtmContainerId?: string;
  apiKey: string;
  authDomain: string;
  databaseURL: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

const FIREBASE_DEV_CONFIG: FirebaseConfig = firebaseAnalyticsCredentials.development;
const FIREBASE_PROD_CONFIG: FirebaseConfig = firebaseAnalyticsCredentials.production;

export const getFirebaseAnalyticsProfile = (): 'development' | 'production' => (
  environment === EnvironmentType.production ? 'production' : 'development'
);

export const getFirebaseConfig = (): FirebaseConfig => {
  const isProd = environment === EnvironmentType.production;
  const config = isProd ? FIREBASE_PROD_CONFIG : FIREBASE_DEV_CONFIG;

  if (isProd && config.measurementId === FIREBASE_DEV_CONFIG.measurementId && config.measurementId) {
    console.error(
      '[GA4] ERRO: measurementId de produção igual ao DEV. ' +
        'Atualize firebase-analytics.credentials.json → production.',
    );
  }

  if (isProd && firebaseAnalyticsProfile.profile !== 'production') {
    console.error(
      '[GA4] ERRO: perfil GA4 pinado não é production. ' +
        `Atual: ${firebaseAnalyticsProfile.profile} (fonte: ${firebaseAnalyticsProfile.source}). ` +
        'Execute: node scripts/pin-firebase-analytics-profile.js production',
    );
  }

  return config;
};

/** Moeda padrão para todos os eventos de e-commerce GA4. */
export const GA4_CURRENCY = 'BRL';

/** Janela de deduplicação page_view (ms). Evita duplicata em NavigationEnd repetido. */
export const PAGE_VIEW_DEDUP_MS = 2_000;

/** Normaliza URL de rota (sem query string). */
export const normalizeRoutePath = (url: string): string => (url || '').split('?')[0] || '/';

/**
 * Divide a URL da imagem do banner em domínio (origin) + path.
 *
 * O GA4 trunca o valor das dimensões ao gravar, e a URL completa do criativo
 * estourava esse limite: os relatórios recebiam a URL cortada e não conseguiam
 * renderizar a imagem do banner. Enviando `promotion_name` = domínio e
 * `creative_name` = path, cada parte cabe no limite e o relatório (Looker
 * Studio) remonta a URL concatenando promotion_name + creative_name.
 *
 * Sem domínio reconhecível (URL relativa), o valor inteiro vai no path para a
 * concatenação continuar devolvendo exatamente a string original.
 */
export const splitBannerImageUrl = (imageUrl?: string): { origin: string; path: string } => {
  const url = (imageUrl ?? '').trim();
  if (!url) return { origin: '', path: '' };

  const match = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]+)(.*)$/i.exec(url);
  return match ? { origin: match[1], path: match[2] } : { origin: '', path: url };
};

/**
 * Rotas em que o contexto de marca do catálogo é válido para GA4
 * (listagem de produtos da marca e detalhe do produto).
 */
export const isCatalogBrandContextPath = (url: string): boolean => {
  const path = normalizeRoutePath(url);
  return path.startsWith('/tabs/catalog/products') || path.startsWith('/tabs/pp/catalog/products');
};

export const isCatalogProductDetailPath = (url: string): boolean => {
  const path = normalizeRoutePath(url);
  return path.includes('/catalog/products/details') || path.includes('/pp/catalog/products/details');
};

/** Listagem de produtos (sem detalhe). */
export const isCatalogProductsListPath = (url: string): boolean => (
  isCatalogBrandContextPath(url) && !isCatalogProductDetailPath(url)
);

// ─────────────────────────────────────────────────────────────────────────────
// Produto Sugerido (lista de recomendação)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Constantes da lista "Produto Sugerido". Fonte única para o serviço e o mapper —
 * o nome da lista precisa ser idêntico do view_item_list até o purchase para o
 * funil (exibição > clique > carrinho > compra) fechar no GA4/BigQuery.
 */
export const SUGGESTED_LIST = {
  ID: 'recommended_products',
  NAME: 'Produto Sugerido',
  POINTS_ID: 'recommended_points_products',
  POINTS_NAME: 'Produto Sugerido - Clube Orbitta',
  ITEM_ORIGIN: 'produto_sugerido',
  /** Sistema de recomendação que gerou a lista (versionável: _v1A, _v1B em teste A/B). */
  SOURCE: 'product_recommended_api',
  /** add_method do "Adicionar todos" da seção Produto Sugerido. */
  BULK_ADD_METHOD: 'bulk_add_from_recommendation',
} as const;

/**
 * Janela em que o clique num card sugerido ainda atribui à lista o add_to_cart
 * feito em outra tela (o card leva para o detalhe/modal e a adição acontece lá).
 * Mesma janela do clique de banner, por coerência de leitura do funil.
 */
export const SUGGESTED_CLICK_TTL_MS = 30 * 60 * 1000;

/**
 * Local estável por rota, independente da categoria/marca guardada no carrinho.
 * Segue os títulos em português do Analytics; o nome base já distingue o Clube.
 */
export const suggestedListPlacement = (url: string): string => {
  const catalogPrefix = normalizeRoutePath(url).startsWith('/tabs/pp/') ? '' : 'Catálogo - ';
  const path = normalizeRoutePath(url).split('#')[0].replace(/\/$/, '').replace(/^\/tabs\/pp(?=\/|$)/, '/tabs');
  if (path.startsWith('/tabs/catalog/products/details')) return `${catalogPrefix}Detalhe do Produto`;
  if (path === '/tabs/catalog/products') return `${catalogPrefix}Produtos`;
  if (path === '/tabs/catalog/brands') return `${catalogPrefix}Marcas`;
  if (path === '/tabs/catalog' || path === '/tabs/orbitta-categories') return `${catalogPrefix}Categorias`;
  if (path.startsWith('/tabs/cart-redeem')) return 'Carrinho Resgate';
  if (path.startsWith('/tabs/cart')) return 'Carrinho';
  if (path === '/tabs/home' || path === '/tabs') return 'Home';
  return '';
};
